import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, XCircle, Loader, ArrowRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    pollStatus(0);
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) { setStatus("timeout"); return; }
    try {
      const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("success");
        setDetails(data);
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <>
      <Helmet><title>Payment Status | Dispatch Hunts</title></Helmet>
      <section className="min-h-screen bg-carbon grain flex items-center justify-center pt-24 pb-16" data-testid="payment-success-page">
        <div className="text-center max-w-lg">
          {status === "checking" && (
            <>
              <Loader className="w-12 h-12 text-brass mx-auto mb-6 animate-spin" />
              <h1 className="font-display text-3xl font-bold text-bone mb-4">Processing payment...</h1>
              <p className="font-body text-bone/50">Verifying your deposit. This may take a moment.</p>
            </>
          )}

          {status === "success" && (
            <div data-testid="payment-confirmed">
              <CheckCircle className="w-16 h-16 text-field mx-auto mb-6" />
              <h1 className="font-display text-3xl font-bold text-bone mb-4">Deposit Confirmed</h1>
              <p className="font-body text-bone/50 mb-2">
                Your ${details ? (details.amount_total / 100).toLocaleString() : ""} deposit has been received.
              </p>
              <p className="font-body text-bone/30 text-sm mb-8">
                Our team will reach out within 24 hours with next steps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" className="btn-brass">Back to Home <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/expeditions" className="btn-outline">Browse More Expeditions</Link>
              </div>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div data-testid="payment-error">
              <XCircle className="w-16 h-16 text-copper mx-auto mb-6" />
              <h1 className="font-display text-3xl font-bold text-bone mb-4">
                {status === "expired" ? "Session Expired" : "Payment Issue"}
              </h1>
              <p className="font-body text-bone/50 mb-8">
                {status === "expired" ? "Your payment session has expired." : "We couldn't verify your payment. Please try again or contact us."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/deposit" className="btn-brass">Try Again</Link>
                <a href="tel:4065805366" className="btn-outline">Call: 406-580-5366</a>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
